import { useMutation } from '@tanstack/react-query';

import { useContext } from 'react';
import { ApiContext } from '@shared/contexts';

import { TCompleteAnalysisDto, TImpactAnalysis } from './types';
import { TUseDocumentResult, TVersion } from '@modules/documents';
import { TFileVersion, TUseFileResult } from '@modules/files';
import { docOrFileKey } from '@modules/items/helpers/itemKeys.helper';

// --- POST:

export const useCompleteAnalysis = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async (body: TCompleteAnalysisDto): Promise<TImpactAnalysis> =>
    await apiClient.post(`/tags/impacts/analysis`, body).then((res) => res.data);

  /**
   * Updates the impact analysis for a document or file version
   * @param oldData - Existing query data
   * @param itemKey - Key to update ('doc' or 'file')
   * @param versionId - ID of the version to update
   * @param newImpactAnalysis - New impact analysis data
   */
  const updateImpactAnalysis = <T>(
    oldData: T | undefined,
    itemKey: keyof T,
    versionId: string,
    newImpactAnalysis: TImpactAnalysis
  ): typeof oldData => {
    if (!oldData) return oldData;

    return {
      ...oldData,
      [itemKey]: {
        ...oldData[itemKey],
        versions: (oldData[itemKey] as any).versions.map((version: TVersion | TFileVersion) =>
          version.id === versionId ? { ...version, impactAnalysis: newImpactAnalysis } : version
        ),
      },
    };
  };

  return useMutation({
    mutationFn,
    onSuccess: (newImpactAnalysis, variables) => {
      const { itemId, itemtype, versionId } = variables;

      const queryKey = itemtype === 'DOCUMENT' ? `document-${itemId}` : `file-${itemId}`;
      const itemKey = docOrFileKey(itemtype);

      queryClient.setQueryData<TUseDocumentResult | TUseFileResult>([queryKey], (oldData) =>
        updateImpactAnalysis(oldData, itemKey as keyof typeof oldData, versionId, newImpactAnalysis)
      );

      show('success', 'Finished impact analysis.');
    },
    onError: handleApiError('Failed to finish impact analysis!'),
  });
};
